import { useEffect, useState, createRef } from "react";
import { disableMapEvents, addLayerToMap } from "../utils/mapFunctions";

const ExternalControl = (props) => {
  const map = props.map;
  const layers = props.layers;
  const panelRef = createRef();
  const [inputValue, setInputValue] = useState("");
  const [serviceInfo, setServiceInfo] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    disableMapEvents(panelRef);
  }, [panelRef]);

  const loadLayers = async (url) => {
    if (!isValidUrl(url)) {
      setError("Invalid URL");
      return;
    } else if (!url.toLowerCase().includes("wms") && !url.toLowerCase().includes("wmts") && !url.toLowerCase().includes("wfs")) {
      setError("Enter a valid WMS, WMTS, or WFS map service");
      return;
    }
    setIsLoading(true);
    setError(null);
    setServiceInfo(null);

    try {
      const serviceMetadata = await fetchServiceMetadata(url);
      setServiceInfo(serviceMetadata);
    } catch (err) {
      setError("Error loading layers");
      console.error("Error loading service:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const isValidUrl = (string) => {
    try {
      new URL(string);
      return true;
    } catch (_) {
      return false;
    }
  };

  const getNextExtNum = (layers) => {
    const extNums = layers
      .map((l) => (typeof l.id === "string" && l.id.startsWith("ext-") ? parseInt(l.id.replace("ext-", ""), 10) : NaN))
      .filter((n) => !isNaN(n));

    const max = extNums.length ? Math.max(...extNums) : -1;
    return max + 1;
  };

  const getOperationUrl = (operationsEl, name) => {
    const owsNS = "http://www.opengis.net/ows/1.1";
    const xlinkNS = "http://www.w3.org/1999/xlink";

    const operations = operationsEl?.getElementsByTagNameNS(owsNS, "Operation") || [];
    const op = Array.from(operations).find((el) => el.getAttribute("name") === name);
    const getEl = op?.getElementsByTagNameNS(owsNS, "Get")[0];

    return getEl?.getAttributeNS(xlinkNS, "href") || "";
  }

  const parseWMTSCapabilities = (xml, url) => {
    const owsNS = "http://www.opengis.net/ows/1.1";

    const contents = xml.getElementsByTagName("Contents")[0];
    const layerElements = contents?.getElementsByTagName("Layer") || [];

    const operations = xml.getElementsByTagNameNS(owsNS, "OperationsMetadata")[0];
    const getTile = getOperationUrl(operations, "GetTile");
    const getFeatureInfo = getOperationUrl(operations, "GetFeatureInfo");

    const baseIndex = getNextExtNum(props.layers);

    const layers = Array.from(layerElements).map((layerEl, i) => {
      const identifier = layerEl.getElementsByTagNameNS(owsNS, "Identifier")[0]?.textContent || "";
      const title = layerEl.getElementsByTagNameNS(owsNS, "Title")[0]?.textContent || identifier;

      const bboxEl = layerEl.getElementsByTagNameNS(owsNS, "WGS84BoundingBox")[0];
      let extent = "";
      if (bboxEl) {
        const lowerCorner = bboxEl.getElementsByTagNameNS(owsNS, "LowerCorner")[0]?.textContent.split(" ") || [];
        const upperCorner = bboxEl.getElementsByTagNameNS(owsNS, "UpperCorner")[0]?.textContent.split(" ") || [];
        extent = `${lowerCorner[0]},${lowerCorner[1]},${upperCorner[0]},${upperCorner[1]}`;
      }

      const matrixSetLinks = Array.from(layerEl.getElementsByTagName("TileMatrixSetLink"))
        .map(link => {
          const matrixSet = link.getElementsByTagName("TileMatrixSet")[0]?.textContent || "";

          const allMatrixSets = xml.getElementsByTagName("TileMatrixSet");
          const thisMatrixSet = Array.from(allMatrixSets).find(ms => {
            const id = ms.getElementsByTagNameNS(owsNS, "Identifier")[0]?.textContent;
            return id === matrixSet;
          });

          const firstTileMatrixId = thisMatrixSet
            ?.getElementsByTagName("TileMatrix")[0]
            ?.getElementsByTagNameNS(owsNS, "Identifier")[0]
            ?.textContent;

          const usePrefix = firstTileMatrixId?.startsWith(matrixSet + ":");

          return {
            TileMatrixSet: matrixSet,
            TileMatrix: usePrefix ? `${matrixSet}:\${z}` : `\${z}`
          };
        });

      let start_datetime = "", end_datetime = "";
      const dimensionEls = Array.from(layerEl.getElementsByTagName("Dimension"));
      const dimension = dimensionEls.find((d) =>
        d.getAttribute("name") === "time" ||
        d.getElementsByTagNameNS(owsNS, "Identifier")[0]?.textContent.toLowerCase() === "time"
      );

      if (dimension) {
        const rawText = dimension.textContent.trim();
        if (rawText.includes("/")) {
          const [start, end] = rawText.split("/");
          start_datetime = start || "";
          end_datetime = end || "";
        } else {
          const values = Array.from(dimension.getElementsByTagName("Value")).map((v) => v.textContent).filter(Boolean);
          if (values.length) {
            start_datetime = values[0];
            end_datetime = values[values.length - 1];
          }
        }
      }

      return {
        id: identifier,
        num_layer: "ext-" + (baseIndex + i),
        unit: "",
        abbreviation: identifier,
        standardName: null,
        name_en: title,
        layer_name: identifier,
        dim_id: dimension ? "time" : "",
        TileMatrixSetLink: matrixSetLinks,
        extent,
        start_datetime,
        end_datetime
      };
    });

    return {
      label: layers[0]?.abbreviation || "Unnamed Layer",
      id: layers[0]?.id || "unknown",
      url,
      map_service: "WMTS",
      extent: layers[0]?.extent || "",
      start_datetime: layers[0]?.start_datetime || "",
      end_datetime: layers[0]?.end_datetime || "",
      freq_title: "Multi-sensor observations",
      freq_period: "day",
      GetTile: getTile,
      GetMap: "",
      GetFeatureInfo: getFeatureInfo,
      GetLegendGraphic: "",
      GetCapabilities: url,
      layers
    };
  }

  const parseWMSCapabilities = (xml, url) => {
    const xlinkNS = "http://www.w3.org/1999/xlink";

    const capability = xml.getElementsByTagName("Capability")[0];
    const request = capability?.getElementsByTagName("Request")[0];
    let GetMap = request?.getElementsByTagName("GetMap")[0]
      ?.getElementsByTagName("OnlineResource")[0]
      ?.getAttributeNS(xlinkNS, "href") || "";
    if (!GetMap) {
      let baseUrl = url.split("?")[0];
      GetMap = baseUrl;
    }
    const GetFeatureInfo = request?.getElementsByTagName("GetFeatureInfo")[0]
      ?.getElementsByTagName("OnlineResource")[0]
      ?.getAttributeNS(xlinkNS, "href") || "";
    const GetLegendGraphic = request?.getElementsByTagName("GetLegendGraphic")[0]
      ?.getElementsByTagName("OnlineResource")[0]
      ?.getAttributeNS(xlinkNS, "href") || "";

    const baseIndex = getNextExtNum(props.layers);

    const xmlLayers = Array.from(xml.getElementsByTagName("Layer")).filter((l) => l.getElementsByTagName("Name").length);

    const layers = xmlLayers.map((layerEl, i) => {
      const name = layerEl.getElementsByTagName("Name")[0]?.textContent || "";
      const title = layerEl.getElementsByTagName("Title")[0]?.textContent || name;

      const bbox = layerEl.getElementsByTagName("EX_GeographicBoundingBox")[0];
      let extent = "";
      if (bbox) {
        const west = bbox.getElementsByTagName("westBoundLongitude")[0]?.textContent;
        const south = bbox.getElementsByTagName("southBoundLatitude")[0]?.textContent;
        const east = bbox.getElementsByTagName("eastBoundLongitude")[0]?.textContent;
        const north = bbox.getElementsByTagName("northBoundLatitude")[0]?.textContent;
        extent = `${west},${south},${east},${north}`;
      }

      const dimensionEls = Array.from(layerEl.getElementsByTagName("Dimension"));
      const dimension = dimensionEls.find((d) =>
        d.getAttribute("name") === "time" ||
        d.getElementsByTagName("ows:Identifier")[0]?.textContent?.toLowerCase() === "time"
      );

      let start_datetime = "", end_datetime = "";
      if (dimension) {
        const rawText = dimension.textContent.trim();
        if (rawText.includes("/")) {
          const [start, end] = rawText.split("/");
          start_datetime = start || "";
          end_datetime = end || "";
        } else {
          const values = rawText.split(",").map((v) => v.trim()).filter(Boolean);
          if (values.length) {
            start_datetime = values[0];
            end_datetime = values[values.length - 1];
          }
        }
      }

      return {
        id: name,
        num_layer: "ext-" + (baseIndex + i),
        unit: "",
        abbreviation: name,
        standardName: null,
        name_en: title,
        layer_name: name,
        dim_id: dimension ? "time" : "",
        start_datetime,
        end_datetime,
        TileMatrixSetLink: [],
        extent
      };
    });

    return {
      label: layers[0]?.abbreviation || "Unnamed Layer",
      id: layers[0]?.id || "unknown",
      url,
      map_service: "WMS",
      extent: layers[0]?.extent || "",
      start_datetime: layers[0]?.start_datetime || "",
      end_datetime: layers[0]?.end_datetime || "",
      freq_title: "",
      freq_period: "",
      GetTile: "",
      GetMap,
      GetFeatureInfo,
      GetLegendGraphic,
      GetCapabilities: url,
      layers
    };
  }

  const parseWFSCapabilities = (xml, url) => {
    const featureTypeList = xml.getElementsByTagName("FeatureTypeList")[0];
    const featureTypes = Array.from(featureTypeList.getElementsByTagName("FeatureType"));

    const layers = featureTypes.map((typeEl, i) => {
      const name = typeEl.getElementsByTagName("Name")[0]?.textContent || "";
      const title = typeEl.getElementsByTagName("Title")[0]?.textContent || name;

      const bbox = typeEl.getElementsByTagName("WGS84BoundingBox")[0];
      let extent = "";
      if (bbox) {
        const west = bbox.getElementsByTagName("westBoundLongitude")[0]?.textContent;
        const south = bbox.getElementsByTagName("southBoundLatitude")[0]?.textContent;
        const east = bbox.getElementsByTagName("eastBoundLongitude")[0]?.textContent;
        const north = bbox.getElementsByTagName("northBoundLatitude")[0]?.textContent;
        extent = `${west},${south},${east},${north}`;
      }

      return {
        id: name,
        num_layer: "ext-" + (getNextExtNum(props.layers) + i),
        unit: "",
        abbreviation: name,
        standardName: null,
        name_en: title,
        layer_name: name,
        extent,
      };
    });

    return {
      label: layers[0]?.abbreviation || "Unnamed Layer",
      id: layers[0]?.id || "unknown",
      GetMap: url.split('?')[0],
      map_service: "WFS",
      extent: layers[0]?.extent || "",
      GetCapabilities: url,
      layers
    };
  };

  const fetchWithTimeout = (url, timeout) => {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeout);
    return fetch(url, { signal: controller.signal }).finally(() => clearTimeout(timer));
  };

  const fetchServiceMetadata = async (url) => {
    let service = url.toLowerCase().includes("wms") ? "wms" : url.toLowerCase().includes("wmts") ? "wmts" : "wfs";
    let cleanUrl = url.replace(/[?&]$/, "");
    cleanUrl = cleanUrl.replace(/([?&])version=[0-9.]+/i, "");
    const hasService = cleanUrl.toLowerCase().includes("service=");
    const capsUrl = hasService
      ? `${cleanUrl}&request=GetCapabilities&version=1.0.0`
      : `${cleanUrl}?service=${service}&request=GetCapabilities&version=1.0.0`;

    const response = await fetchWithTimeout(capsUrl, 10000);
    if (!response.ok) throw new Error("Error fetching GetCapabilities");

    const xmlText = await response.text();

    if (xmlText.length > 10_000_000) {
      throw new Error("Capabilities file too large");
    }

    const xml = new DOMParser().parseFromString(xmlText, "application/xml");

    const parserError = xml.getElementsByTagName("parsererror");
    if (parserError.length > 0) {
      throw new Error("Invalid XML response");
    }

    const isWMTS = xml.getElementsByTagNameNS("http://www.opengis.net/ows/1.1", "ServiceIdentification").length > 0;
    const isWFS = xml.getElementsByTagName("FeatureTypeList").length > 0;

    if (isWFS) {
      return parseWFSCapabilities(xml, capsUrl);
    }

    return isWMTS
      ? parseWMTSCapabilities(xml, capsUrl)
      : parseWMSCapabilities(xml, capsUrl);
  }

  const handleAddLayer = async (url, id, title, layerName, layerData) => {
    const layer = await addLayerToMap(map, layers, url, id, layerName, layerData);
    if (layer) {
      props.addLayer(layer, url, id, title, layerName);
    } else {
      console.log("Error loading layer");
    }
  }

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      loadLayers(inputValue);
    }
  };

  const handleClearSearch = () => {
    setInputValue("");
    setServiceInfo(null);
    setError(null);
  };

  return (
    <div className="widget-panel-overlay" ref={panelRef}>
      <div className="widget-title">
        External services
        <button className="icon-button widget-close" onClick={() => props.setShowExternal(false)} aria-label="Close">
          <i className="fa-solid fa-xmark"></i>
        </button>
      </div>
      <div className="external-container">
        <div className="widget-text">Add WMS, WMTS and WFS external services by URL and select layers below</div>
        <div className="widget-input external-input">
          <input
            type="text"
            placeholder="External service URL"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
          />
          {inputValue.length > 0 && (
            <button className="widget-input-clear" onClick={handleClearSearch} aria-label="Clear input">
              <i className="fa-solid fa-xmark"></i>
            </button>
          )}
        </div>
        <button onClick={() => loadLayers(inputValue)} disabled={isLoading} className="ccl-button ccl-button--default" aria-label="Load layers">
          <span>{isLoading ? "Loading..." : "Load"}</span>
        </button>
        {error && <div className="error-message">{error}</div>}
        {!error && serviceInfo && (
          <>
            <hr />
            <div className="widget-subtitle">Layers</div>
            <div className="external-layers">
              {serviceInfo.layers.map((layer, i) => (
                <div className="datasets-layer" key={i}>
                  <div>
                    {layer.name_en}
                    {layer.unit && <i> ({layer.unit}) </i>}
                  </div>
                  <div className="catalogue-buttons">
                    <button
                      className="ccl-button ccl-button--default"
                      disabled={layers.some((a) => a.id === layer.num_layer)}
                      onClick={() => handleAddLayer(serviceInfo.GetTile || serviceInfo.GetMap, layer.num_layer, layer.name_en, layer.layer_name, {...layer, url: serviceInfo.GetTile || serviceInfo.GetMap, type: serviceInfo.map_service})}
                      aria-label="Add layer"
                    >
                      {layers.some((a) => a.id === layer.num_layer) ? "Added" : "Add"}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ExternalControl;
